import { TOTP_CODE_LENGTH } from '@gozoki/api-types/src/validation/adminMfa'
import { tryCatch } from '@gozoki/tools'
import { useMutation } from '@tanstack/react-query'
import { Copy, Download, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { FormErrorAlert } from '~/components/new/app-ui/form-error-alert'
import { CODE_FIELD, UNKNOWN_ERROR_MESSAGE } from '~/components/routes/login/login-session'
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert'
import { Button } from '~/components/ui/button'
import { useAppForm } from '~/hooks/form-hooks'
import { api } from '~/lib/tuyau'
import { getApiErrorMessage } from '~/lib/utils'
import { downloadBlob } from '~/utils/excel/downloadBlob'
import { groupSecret, RECOVERY_CODES_FILE_NAME, recoveryCodesText } from './account-security'

const TOTP_FIELD = CODE_FIELD.totp

interface Provisioning {
  secret: string
  qrCode: string
}

const copyToClipboard = async (text: string, success: string) => {
  const [, error] = await tryCatch(navigator.clipboard.writeText(text))
  if (error) toast.error('Copie impossible')
  else toast.success(success)
}

/** Étape 1 : QR code (ou clé à saisir), puis le premier code de l'application. */
function ScanStep({
  provisioning,
  onCancel,
  onConfirmed,
}: {
  provisioning: Provisioning
  onCancel: () => void
  onConfirmed: (recoveryCodes: string[]) => void
}) {
  const [error, setError] = useState<string | null>(null)
  const { mutateAsync: confirm } = useMutation(api.auth.confirmAdminMfa.mutationOptions())

  const form = useAppForm({
    defaultValues: { code: '' },
    validators: { onSubmit: TOTP_FIELD.schema },
    onSubmit: async ({ value, formApi }) => {
      setError(null)
      const [response, failure] = await tryCatch(confirm({ body: { code: value.code } }))
      if (response === null) {
        formApi.setFieldValue('code', '')
        setError(getApiErrorMessage(failure, UNKNOWN_ERROR_MESSAGE))
        return
      }
      // Le type inclut `undefined` (branche du limiteur, qui répond sans corps typé).
      if (!response?.recoveryCodes) {
        setError(UNKNOWN_ERROR_MESSAGE)
        return
      }
      onConfirmed(response.recoveryCodes)
    },
  })

  return (
    <div className="flex flex-col gap-5">
      <ol className="flex list-decimal flex-col gap-1 pl-5 text-sm">
        <li>
          Scannez ce QR code avec votre application d'authentification (Google Authenticator,
          Microsoft Authenticator, 1Password…).
        </li>
        <li>Saisissez le code à {TOTP_CODE_LENGTH} chiffres qu'elle affiche.</li>
      </ol>

      <div className="flex flex-wrap items-center gap-5">
        <img
          src={provisioning.qrCode}
          alt="QR code du double facteur"
          width={176}
          height={176}
          className="rounded-lg border bg-white p-2"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-2 text-sm">
          <p className="text-muted-foreground">Impossible de scanner ? Saisissez cette clé :</p>
          <code
            className="w-fit rounded-md bg-muted px-2 py-1 font-mono text-sm tracking-wider break-all"
            data-mfa-secret
          >
            {groupSecret(provisioning.secret)}
          </code>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-fit"
            onClick={() => void copyToClipboard(provisioning.secret, 'Clé copiée')}
          >
            <Copy className="size-4" />
            Copier la clé
          </Button>
        </div>
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault()
          event.stopPropagation()
          void form.handleSubmit()
        }}
        className="flex flex-col gap-4"
        noValidate
      >
        <form.AppField name="code">
          {(field) => (
            <field.TextField
              label={TOTP_FIELD.label}
              placeholder={TOTP_FIELD.placeholder}
              inputMode={TOTP_FIELD.inputMode}
              autoComplete="one-time-code"
              className="max-w-48"
              transformValue={TOTP_FIELD.transform}
            />
          )}
        </form.AppField>

        <FormErrorAlert message={error} />

        <div className="flex gap-2">
          <form.AppForm>
            <form.SubmitButton>Activer</form.SubmitButton>
          </form.AppForm>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Annuler
          </Button>
        </div>
      </form>
    </div>
  )
}

/**
 * Étape 2 : les dix codes de secours, montrés cette seule fois. La page est rechargée ensuite :
 * l'activation a fermé les autres sessions du compte, et ses connexions temps réel avec elles
 * (`confirmAdminMfa`) ; la session courante rouvre les siennes, et un compte contraint retrouve
 * le back-office.
 */
function RecoveryCodesStep({ codes }: { codes: string[] }) {
  const text = recoveryCodesText(codes, window.location.host, new Date())

  const download = () =>
    downloadBlob({
      blob: new Blob([text], { type: 'text/plain;charset=utf-8' }),
      filename: RECOVERY_CODES_FILE_NAME,
    })

  return (
    <div className="flex flex-col gap-4">
      <Alert variant="success">
        <ShieldCheck />
        <AlertTitle>Double facteur activé</AlertTitle>
        <AlertDescription>
          Notez ces codes de secours maintenant : ils ne seront plus affichés. Chacun ouvre une
          session une seule fois, si vous n'avez plus votre téléphone.
        </AlertDescription>
      </Alert>

      <ul
        aria-label="Codes de secours"
        className="grid grid-cols-2 gap-x-6 gap-y-1.5 rounded-lg border bg-muted/40 p-4 font-mono text-sm tracking-wider"
      >
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => void copyToClipboard(codes.join('\n'), 'Codes copiés')}
        >
          <Copy className="size-4" />
          Copier
        </Button>
        <Button type="button" variant="outline" onClick={download}>
          <Download className="size-4" />
          Télécharger
        </Button>
        <Button type="button" className="ml-auto" onClick={() => window.location.reload()}>
          J'ai noté mes codes
        </Button>
      </div>
    </div>
  )
}

/**
 * Enrôlement au double facteur de l'admin connecté (S1.4b) : secret et QR code
 * (`POST /auth/mfa-admin/enroll`), confirmation par un premier code
 * (`POST /auth/mfa-admin/confirm`), puis les codes de secours. Sert l'écran « Sécurité du compte »
 * et l'enrôlement obligatoire.
 */
export function MfaEnrollment() {
  const [provisioning, setProvisioning] = useState<Provisioning | null>(null)
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)

  const { mutate: enroll, isPending } = useMutation(
    api.auth.enrollAdminMfa.mutationOptions({
      onError: (error) => toast.error(getApiErrorMessage(error, UNKNOWN_ERROR_MESSAGE)),
      onSuccess: (response) => {
        if (response) setProvisioning({ secret: response.secret, qrCode: response.qrCode })
      },
    }),
  )

  if (recoveryCodes) return <RecoveryCodesStep codes={recoveryCodes} />
  if (provisioning) {
    return (
      <ScanStep
        provisioning={provisioning}
        onCancel={() => setProvisioning(null)}
        onConfirmed={setRecoveryCodes}
      />
    )
  }
  return (
    <Button type="button" className="w-fit" onClick={() => enroll({})} disabled={isPending}>
      <ShieldCheck className="size-4" />
      Activer le double facteur
    </Button>
  )
}
